import { TrashIcon } from "@heroicons/react/20/solid";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ReactComponent as NewMessageIcon } from "assets/img/inbox/new.svg";
import { MarkdownContent, Spinner, TablePagination } from "components/elements";
import { DeleteDialogModal } from "components/elements/DeleteDialogModal";
import { useCurrentUser } from "hooks/useCurrentUser";
import { cn } from "lib/cn";
import { formatISODateRelative } from "lib/formatISODate";
import { trpc } from "lib/trpc";
import { ChatArea } from "./ChatArea";
import { ConversationDropdown } from "./ConversationDropdown";
import { NoMessages } from "./NoMessages";

interface Participant {
    id: string;
    firstName: string;
    lastName: string;
    avatar: string | null;
    isSocialEnabled: boolean;
    isPrivateEnabled?: boolean;
}

interface Conversation {
    id: string;
    unreadCount: number;
    participants: Participant[];
    lastMessage: {
        id: string;
        content: string;
        senderId: string;
        receiverId: string;
        createdAt: string;
        read: boolean;
    } | null;
}

export const Messages = () => {
    const currentUser = useCurrentUser();
    const userId = currentUser?.id.toString();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filter, setFilter] = useState<"all" | "unread">("all");
    const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
    const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const { id: messageId } = useParams();
    const navigate = useNavigate();
    const queryClient = trpc.useContext();

    const activeConversationId = messageId ?? selectedConversation;

    const { data, isPending: isLoading } = trpc.messages.getConversations.useQuery({
        userId: userId || "",
        page,
        pageSize,
    });
    const conversations = data?.conversations ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.ceil(total / pageSize);

    const deleteConversationMutation = trpc.messages.deleteConversation.useMutation({
        onSuccess: () => {
            queryClient.messages.getConversations.invalidate();
            setSelectedConversation(null);
            setConversationToDelete(null);
        },
        onError: () => {
            setConversationToDelete(null);
        },
    });

    const handleSelectConversation = (conversation: Conversation) => {
        setSelectedConversation(conversation.id);
    };

    if (!userId) {
        return <div>No user selected</div>;
    }

    if (isLoading) {
        return (
            <span className="absolute inset-0 flex items-center justify-center">
                <Spinner className="h-10 w-10 text-slate-500" />
            </span>
        );
    }

    return (
        <div className="mx-auto flex h-screen w-full flex-col justify-center">
            {conversations.length > 0 ? (
                <div className="flex flex-col">
                    <div className=" flex h-[calc(100vh-theme(space.12))] w-full justify-between gap-6 pb-5">
                        {/* Sidebar with a list of the diff messages */}
                        <div className="no-scrollbar w-1/3 shrink-0 overflow-y-auto rounded-2xl border border-[#d5d5d5] bg-white px-0 py-4">
                            <div className="px-4 pt-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-[20px] font-bold text-[#424242]">
                                        My Inbox
                                    </div>
                                    {/* Segmented toggle: Unread / Messages (right-aligned) */}
                                    <div className="ml-2 flex items-center rounded-lg bg-[#c8c8c8] p-0.5">
                                        <button
                                            onClick={() => setFilter("unread")}
                                            className={cn(
                                                "rounded-lg px-3 py-1 text-sm font-medium transition-all",
                                                {
                                                    "bg-[#FAFFFE] text-[#707070]":
                                                        filter === "unread",
                                                    "text-[#707070]": filter !== "unread",
                                                },
                                            )}
                                        >
                                            Unread
                                        </button>
                                        <button
                                            onClick={() => setFilter("all")}
                                            className={cn(
                                                "rounded-lg px-3 py-1 text-sm font-medium transition-all",
                                                {
                                                    "bg-[#FAFFFE] text-[#707070]": filter === "all",
                                                    "text-[#707070]": filter !== "all",
                                                },
                                            )}
                                        >
                                            Messages
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 flex items-center justify-end ">
                                    <div className="relative">
                                        <div
                                            className="text-normal flex items-center bg-inherit text-[#424242]"
                                            onClick={() => {
                                                navigate({ to: "/messages" });
                                                setSelectedConversation(null);
                                                setIsModalOpen(true);
                                            }}
                                        >
                                            <span>New Message</span>
                                            <span className="ml-1 inline-flex h-6 w-6 cursor-pointer items-center justify-center">
                                                <NewMessageIcon className="h-4 w-4 text-[#0F8EA6]" />
                                            </span>
                                        </div>
                                        {isModalOpen && (
                                            <ConversationDropdown
                                                isOpen={isModalOpen}
                                                setIsOpen={setIsModalOpen}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 space-y-0 border-t border-[#d5d5d5]">
                                {(filter === "unread"
                                    ? conversations.filter((c: any) => c.unreadCount > 0)
                                    : conversations
                                ).map((conversation: Conversation) => {
                                    const participant = conversation.participants.find(
                                        (p: Participant) => p.id !== userId,
                                    );
                                    if (!participant) return null;

                                    // const hasUnreadMessages = conversation.unreadCount > 0;

                                    const isLastMessageFromOtherUnread =
                                        conversation.lastMessage &&
                                        conversation.lastMessage.senderId !== userId &&
                                        !conversation.lastMessage.read;

                                    return (
                                        <div
                                            key={conversation.id}
                                            onClick={() => handleSelectConversation(conversation)}
                                            className={cn(
                                                "relative flex cursor-pointer items-start space-x-4 rounded-none border-b border-[#d5d5d5] px-4 py-3 transition-colors duration-200",
                                                {
                                                    "bg-[#F0F0F0]":
                                                        activeConversationId === conversation.id,
                                                    "bg-[#DFF1FA] hover:bg-[#DFF1FA]":
                                                        isLastMessageFromOtherUnread &&
                                                        selectedConversation !== conversation.id,
                                                    "bg-[#FAFFFE] hover:bg-gray-100":
                                                        !isLastMessageFromOtherUnread &&
                                                        selectedConversation !== conversation.id,
                                                },
                                            )}
                                        >
                                            {/* Avatar */}
                                            <div className="h-[60px] w-[60px] overflow-hidden rounded-xl">
                                                {participant.avatar ? (
                                                    <img
                                                        src={participant.avatar}
                                                        alt={participant.firstName}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full w-full items-center justify-center bg-gray-300 font-semibold text-white">
                                                        {participant.firstName?.[0]?.toUpperCase() ||
                                                            "U"}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Conversation Info */}
                                            <div className="flex-1 overflow-hidden">
                                                <div className="flex items-center justify-between">
                                                    <div
                                                        className={cn(
                                                            "text-normal truncate font-semibold",
                                                            {
                                                                "text-[#424242]":
                                                                    !isLastMessageFromOtherUnread,
                                                                " text-[#424242]":
                                                                    isLastMessageFromOtherUnread,
                                                                italic: !participant.isPrivateEnabled,
                                                            },
                                                        )}
                                                    >
                                                        {`${participant.firstName} ${participant.lastName}`}
                                                        {!participant.isSocialEnabled &&
                                                            " (inactive)"}
                                                    </div>

                                                    {/* RIGHT SIDE: badges + trash icon */}
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className={cn(
                                                                "shrink-0 text-sm font-normal leading-[22px]",
                                                                {
                                                                    "text-brand-black5":
                                                                        !isLastMessageFromOtherUnread,
                                                                    " text-brand-black5":
                                                                        isLastMessageFromOtherUnread,
                                                                },
                                                            )}
                                                        >
                                                            {isLastMessageFromOtherUnread && (
                                                                <div className="flex flex-row gap-1">
                                                                    <span className=" text-brand-black1 rounded-md border border-[#0DAAF4] bg-[#92CEE4] px-1 py-0.5 text-[11px] font-medium">
                                                                        New
                                                                    </span>
                                                                    <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-medium text-white">
                                                                        {conversation.unreadCount}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Trash icon */}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setConversationToDelete(
                                                                    conversation.id,
                                                                );
                                                            }}
                                                            disabled={
                                                                deleteConversationMutation.isPending
                                                            }
                                                            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-200"
                                                            title="Delete Conversation"
                                                        >
                                                            <TrashIcon className="h-4 w-4 text-red-500" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div
                                                        className={cn("mt-1 truncate text-sm", {
                                                            "text-gray-600":
                                                                !isLastMessageFromOtherUnread,
                                                            "font-medium text-blue-700":
                                                                isLastMessageFromOtherUnread,
                                                        })}
                                                    >
                                                        <MarkdownContent
                                                            markdown={
                                                                conversation.lastMessage?.content ||
                                                                ""
                                                            }
                                                        />
                                                    </div>

                                                    {/*conversation.lastMessage &&
                                                        new Date(
                                                            conversation.lastMessage.createdAt,
                                                        ).toLocaleString([], {
                                                            month: "short",
                                                            day: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: true,
                                                        })*/}
                                                    <div className="font-roboto text-[14px] font-normal leading-normal tracking-[0] text-[#424242]">
                                                        {conversation.lastMessage &&
                                                            formatISODateRelative(
                                                                conversation.lastMessage.createdAt,
                                                            )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="!mt-4">
                                        <TablePagination
                                            total={total}
                                            page={page}
                                            pageSize={pageSize}
                                            onChangePage={(newPage) => setPage(newPage ?? 0)}
                                            onChangePageSize={(newPageSize) => {
                                                setPageSize(newPageSize ?? 10);
                                                setPage(0); // reset to first page on page size change
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Chat Area */}
                        {activeConversationId ? (
                            <div className="min-w-0 flex-1">
                                <ChatArea
                                    userId={userId || ""}
                                    conversationId={activeConversationId.toString()}
                                    setSelectedConversation={setSelectedConversation}
                                />
                            </div>
                        ) : (
                            <div className="h-5/6 flex-1">
                                <NoMessages />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex h-full items-center justify-center p-8">
                    {!isLoading && <NoMessages />}
                </div>
            )}
            <DeleteDialogModal
                open={conversationToDelete !== null}
                setOpen={(isOpen) => {
                    if (!isOpen) setConversationToDelete(null);
                }}
                handleDelete={() => {
                    if (conversationToDelete) {
                        deleteConversationMutation.mutate({
                            conversationId: conversationToDelete,
                        });
                    }
                }}
                disabled={deleteConversationMutation.isPending}
                message="This action cannot be undone. This will permanently delete your message thread from your account."
            />
        </div>
    );
};
